import { createContext, useContext, useState } from 'react'
import type { ComponentType, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Brand } from './ui'
import {
  IconGetReady, IconOrders, IconTickets, IconProfile, IconHelp, IconPerson,
} from '../lib/icons'
import { HelpPanel, type HelpSection } from './HelpPanel'
import { useLocale, type MessageKey } from '../i18n/useLocale'
import './shell.css'

/*
 * V1.9.1 app shell (Phase 0 rebuild) — geometry and values per the design
 * handoff (docs/current/design_handoff_my_trumin_v1_9_1/README.md §"Layout:
 * the app shell" + design/App Shell.dc.html). Replaces the V1 AppShell chrome;
 * routes/screens are untouched. Styling lives in shell.css (logical
 * properties only — the shell mirrors under dir="rtl" from useLocale).
 * DOM ids kept from V1: shell.root / shell.nav / shell.topbar / shell.content
 * / shell.help; testids likewise, so screen tests keep working.
 */

/** Responsive breakpoint — handoff: desktop >= 900px (232px sidebar), else mobile. */
export const MOBILE_BREAKPOINT = 900

/** Shell section names (`passes` is the internal name of Tickets). */
export type Section = 'ready' | 'orders' | 'passes' | 'profile'

const SECTION_PATH: Record<Section, string> = {
  ready: '/get-ready',
  orders: '/orders',
  passes: '/tickets',
  profile: '/profile',
}

export function sectionForPath(pathname: string): Section {
  if (pathname === '/orders' || pathname.startsWith('/orders/')) return 'orders'
  if (pathname === '/tickets' || pathname.startsWith('/tickets/')) return 'passes'
  if (pathname === '/profile' || pathname.startsWith('/profile/')) return 'profile'
  return 'ready'
}

/** Launch gate for the EN/العربية language toggle (Duncan order,
 *  BOARDROOM-2026-09-01-183926): the toggle renders ONLY when
 *  VITE_ARABIC_ENABLED === 'true' — absent/anything-else = HIDDEN, which is
 *  correct for prod with zero env changes. English-in-RTL stays off until real
 *  translations + the settings languages-offered feature land. The i18n
 *  machinery is untouched: dir/RTL support stays intact, and a locale already
 *  stored as `ar` keeps being honoured — there is just no switch to change it. */
export function arabicToggleEnabled(env: Record<string, string | undefined> = import.meta.env): boolean {
  return env.VITE_ARABIC_ENABLED === 'true'
}

/** Viewport seam for the content screens: page h1s do NOT render on mobile
 *  (`showPageHead = !isMobile`). Screens read this via useShellChrome();
 *  default (no shell) = desktop. */
const ShellChromeContext = createContext<{ isMobile: boolean; showPageHead: boolean }>({
  isMobile: false,
  showPageHead: true,
})
export const useShellChrome = () => useContext(ShellChromeContext)

interface NavDef { section: Section; labelKey: MessageKey; testid: string; Icon: ComponentType<{ size?: number }> }
// Exactly four sections, in this order; the fifth mobile tab is Help (panel
// toggle, not a section). Labels resolve through the locale resource.
const NAV: NavDef[] = [
  { section: 'ready', labelKey: 'nav.getReady', testid: 'nav-getready', Icon: IconGetReady },
  { section: 'orders', labelKey: 'nav.orders', testid: 'nav-orders', Icon: IconOrders },
  { section: 'passes', labelKey: 'nav.tickets', testid: 'nav-tickets', Icon: IconTickets },
  { section: 'profile', labelKey: 'nav.profile', testid: 'nav-profile', Icon: IconProfile },
]

// ---------- brand row ----------
// Official flat Trumin lockup (vendored <trumin-logo> component) — NEVER the
// prototype's three.js 3D mark (PRODUCT-RULES.md §Logos) and never a monogram
// tile. dir="ltr": the wordmark is an LTR island inside a mirrored page.
function BrandRow({ markSize, testid }: { markSize: number; testid: string }) {
  return (
    <div data-testid={testid} aria-label="Trumin" dir="ltr" className="shell-brand">
      <Brand size={markSize} />
    </div>
  )
}

// Nav-badge tone: `warn` = amber outstanding-actions count on Get ready
// (handoff §Layout); `success` = the issued-tickets count on Tickets
// (carried over from V1, BOARDROOM-2026-08-14-100859 UX #4).
type BadgeTone = 'warn' | 'success'

function Badge({ count, tone, testid }: { count: number; tone: BadgeTone; testid?: string }) {
  return <span data-testid={testid} className={`shell-badge shell-badge--${tone}`}>{count}</span>
}

// ---------- desktop sidebar nav item ----------
function DesktopNavItem({ label, testid, Icon, active, badge, badgeTestid, badgeTone = 'warn', onClick }: {
  label: string; testid: string; Icon: ComponentType<{ size?: number }>
  active: boolean; badge?: number; badgeTestid?: string; badgeTone?: BadgeTone; onClick: () => void
}) {
  return (
    <button
      data-testid={testid}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      className="shell-navitem"
    >
      <Icon size={20} />
      <span>{label}</span>
      {badge ? <Badge count={badge} tone={badgeTone} testid={badgeTestid} /> : null}
    </button>
  )
}

// ---------- mobile tab (help tab reflects PANEL state, not a section) ----------
function MobileTab({ label, testid, Icon, active, isSection = true, badge, badgeTestid, badgeTone = 'warn', onClick }: {
  label: string; testid: string; Icon: ComponentType<{ size?: number }>
  active: boolean; isSection?: boolean; badge?: number; badgeTestid?: string; badgeTone?: BadgeTone; onClick: () => void
}) {
  return (
    <button
      data-testid={testid}
      aria-current={isSection && active ? 'page' : undefined}
      onClick={onClick}
      className={`shell-tab${!isSection && active ? ' shell-tab--on' : ''}`}
    >
      <Icon size={20} />
      <span>{label}</span>
      {badge ? <Badge count={badge} tone={badgeTone} testid={badgeTestid} /> : null}
    </button>
  )
}

// ---------- shell ----------
export function Shell({ mobile, badgeCount, ticketsBadgeCount = 0, accountName, children }: {
  /** Viewport (caller computes via MOBILE_BREAKPOINT). */
  mobile: boolean
  /** Get-ready nav badge = count of outstanding (`now`) steps across all
   *  events — always DERIVED from live data, never a stored flag. 0 hides it. */
  badgeCount: number
  /** Tickets nav badge = number of ISSUED tickets in the wallet, derived from
   *  the same live source (BOARDROOM-2026-08-14-100859 UX #4). 0 hides it. */
  ticketsBadgeCount?: number
  /** `{firstName} {lastName}` for the account-control tooltip. */
  accountName?: string
  children: ReactNode
}) {
  const [helpOpen, setHelpOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { t, lang, setLang, dir } = useLocale()
  const section = sectionForPath(location.pathname)

  const goTo = (s: Section) => navigate(SECTION_PATH[s])

  const sectionBadge = (s: Section): { count: number; tone: BadgeTone; desktopTestid: string; mobileTestid: string } | null => {
    if (s === 'ready' && badgeCount) {
      return { count: badgeCount, tone: 'warn', desktopTestid: 'nav-getready-badge', mobileTestid: 'tab-getready-badge' }
    }
    if (s === 'passes' && ticketsBadgeCount) {
      return { count: ticketsBadgeCount, tone: 'success', desktopTestid: 'nav-tickets-badge', mobileTestid: 'tab-tickets-badge' }
    }
    return null
  }

  return (
    <ShellChromeContext.Provider value={{ isMobile: mobile, showPageHead: !mobile }}>
      {/* Direction comes from the locale: grid columns follow direction, so
          the desktop sidebar moves to the inline-end on its own in RTL —
          never hand-positioned (handoff §RTL). */}
      <div id="shell.root" dir={dir} className={`shell ${mobile ? 'shell--mobile' : 'shell--desktop'}`}>
        {/* NAV — sidebar (desktop) / bottom tab bar (mobile, 5 equal tabs) */}
        <nav id="shell.nav" className="shell-nav">
          {mobile ? (
            <>
              {NAV.map(d => {
                const b = sectionBadge(d.section)
                return (
                  <MobileTab
                    key={d.section}
                    label={t(d.labelKey)}
                    testid={`tab-${d.testid.slice('nav-'.length)}`}
                    Icon={d.Icon}
                    active={section === d.section}
                    badge={b?.count}
                    badgeTestid={b?.mobileTestid}
                    badgeTone={b?.tone}
                    onClick={() => goTo(d.section)}
                  />
                )
              })}
              {/* Fifth tab is Help — toggles the panel, no route change. */}
              <MobileTab
                label={t('nav.help')}
                testid="tab-help"
                Icon={IconHelp}
                active={helpOpen}
                isSection={false}
                onClick={() => setHelpOpen(o => !o)}
              />
            </>
          ) : (
            <>
              <BrandRow markSize={34} testid="nav-brand" />
              {NAV.map(d => {
                const b = sectionBadge(d.section)
                return (
                  <DesktopNavItem
                    key={d.section}
                    label={t(d.labelKey)}
                    testid={d.testid}
                    Icon={d.Icon}
                    active={section === d.section}
                    badge={b?.count}
                    badgeTestid={b?.desktopTestid}
                    badgeTone={b?.tone}
                    onClick={() => goTo(d.section)}
                  />
                )
              })}
            </>
          )}
        </nav>

        {/* TOPBAR — brand (mobile), then help ? · language · account */}
        <div id="shell.topbar" className="shell-topbar">
          {mobile && <BrandRow markSize={28} testid="topbar-brand" />}
          <div className="shell-topbar-spacer" />
          <button
            data-testid="topbar-help"
            title={t('topbar.help.tip')}
            onClick={() => setHelpOpen(o => !o)}
            className={`shell-topbtn${helpOpen ? ' shell-topbtn--on' : ''}`}
          >
            <IconHelp size={18} />
          </button>
          {/* Language control — EN/العربية toggle; the stored value is the
              account's language preference (uiLang) and drives RTL.
              LAUNCH-HIDDEN behind VITE_ARABIC_ENABLED (Duncan order,
              BOARDROOM-2026-09-01-183926): English-in-RTL stays off until real
              translations + the settings languages-offered feature land. The
              i18n machinery (dir/RTL support, a stored `ar` preference) stays
              fully honoured — only this user-facing switch hides. */}
          {arabicToggleEnabled() && (
            <button
              data-testid="topbar-lang"
              title={t('topbar.lang.tip')}
              onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
              className="shell-topbtn shell-topbtn--lang"
            >
              {t('topbar.lang.toggleLabel')}
            </button>
          )}
          {mobile && (
            // Account control: line-art person icon — never initials/monogram
            // (PRODUCT-RULES.md §Logos). Opens Profile.
            <button
              data-testid="topbar-account"
              title={accountName ? t('topbar.account.tipNamed').replace('{name}', accountName) : t('topbar.account.tip')}
              onClick={() => goTo('profile')}
              className="shell-topbtn"
            >
              <IconPerson size={18} />
            </button>
          )}
        </div>

        {/* CONTENT */}
        <div id="shell.content" className="shell-content">{children}</div>

        {/* HELP — overlay/sheet toggled in place, NEVER a route. */}
        {helpOpen && (
          <div id="shell.help" style={{ display: 'contents' }}>
            <HelpPanel section={section as HelpSection} mobile={mobile} onClose={() => setHelpOpen(false)} />
          </div>
        )}
      </div>
    </ShellChromeContext.Provider>
  )
}
